import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowDown, ArrowUp, ArrowUpDown, Eye, Loader2, Plus, RefreshCw, Search, SlidersHorizontal, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { McfSkuTraceDialog } from "./McfSkuTraceDialog";
import { McfAdjustDialog } from "./McfAdjustDialog";
import { currentUserIsAdmin, errMsg, fmtQty } from "./mcfUtils";

const PAGE_SIZE = 50;
const STATUS_STYLE = {
  "In Stock": "bg-emerald-100 text-emerald-800",
  "Out of Stock": "bg-slate-200 text-slate-700",
  Negative: "bg-rose-100 text-rose-800",
  "No Delivery": "bg-orange-100 text-orange-800",
  "Unmapped SKU": "bg-purple-100 text-purple-800",
};
const COLS = [
  ["sku_id", "SKU ID"], ["sku_name", "SKU Name"], ["delivered_qty", "Delivered Qty.", true], ["consumed_qty", "Consumed Qty.", true],
  ["available_qty", "Available Qty.", true], ["consumption_pct", "Consumption %", true], ["status", "Status"],
];
const pct = (v) => (v === null || v === undefined ? "—" : `${v.toFixed(1)}%`);

function Kpi({ label, value, tone = "text-slate-900", testId }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className={`text-3xl font-bold font-mono ${tone}`} data-testid={testId}>{value}</p>
      </CardContent>
    </Card>
  );
}

function SortHead({ col, sort, setSort }) {
  const [key, label, right] = col;
  const active = sort.key === key;
  const Icon = active ? (sort.dir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <TableHead className={`bg-slate-100 whitespace-nowrap ${right ? "text-right" : ""}`}>
      <button type="button" className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => setSort({ key, dir: active && sort.dir === "asc" ? "desc" : "asc" })} data-testid={`mcf-inv-sort-${key}`}>
        {label}<Icon className={`h-3 w-3 ${active ? "" : "opacity-40"}`} />
      </button>
    </TableHead>
  );
}

export function McfLiveInventory({ warehouse, products }) {
  const isAdmin = currentUserIsAdmin();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sort, setSort] = useState({ key: "sku_id", dir: "asc" });
  const [page, setPage] = useState(1);
  const [traceSku, setTraceSku] = useState(null);
  const [adjust, setAdjust] = useState(null);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const res = await axios.get(`${API}/mcf-inventory/${warehouse.id}`);
      setData(res.data);
    } catch (err) {
      if (!quiet) toast.error(errMsg(err, "Could not load live inventory"));
    } finally {
      setLoading(false);
    }
  }, [warehouse.id]);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 60000);
    const onFocus = () => load(true);
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [load]);
  useEffect(() => setPage(1), [search, statusFilter, sort]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = (data?.rows || []).filter((r) => (!q || r.sku_id.toLowerCase().includes(q) || (r.sku_name || "").toLowerCase().includes(q))
      && (statusFilter === "all" || (statusFilter === "exceptions" ? ["Negative", "No Delivery", "Unmapped SKU"].includes(r.status) : r.status === statusFilter)));
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...list].sort((a, b) => {
      const x = a[sort.key] ?? -Infinity, y = b[sort.key] ?? -Infinity;
      return (typeof x === "string" ? x.localeCompare(y) : x - y) * dir;
    });
  }, [data, search, statusFilter, sort]);
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const t = data?.totals;

  if (loading && !data) {
    return <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" />Loading live inventory…</div>;
  }

  return (
    <div className="space-y-4" data-testid="mcf-live-inventory">
      {!data?.has_ledger && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex gap-2" data-testid="mcf-inv-no-ledger">
          <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
          No live stock recorded for {warehouse.warehouse_name} yet (no GRN received on any stock transfer and no adjustment). Quotation stock validation starts once stock is received.
        </div>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Total Delivered Qty." value={fmtQty(t?.delivered_qty)} testId="mcf-inv-total-delivered" />
        <Kpi label="Total Consumed Qty." value={fmtQty(t?.consumed_qty)} tone="text-[#1F8F88]" testId="mcf-inv-total-consumed" />
        <Kpi label="Total Available Qty." value={fmtQty(t?.available_qty)} tone={t?.available_qty < 0 ? "text-rose-600" : "text-emerald-600"} testId="mcf-inv-total-available" />
        <Kpi label="Overall Consumption %" value={pct(t?.consumption_pct)} testId="mcf-inv-total-pct" />
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-3 flex-wrap">
          <CardTitle className="text-base">
            SKU-wise Live Inventory — {warehouse.warehouse_name} ({warehouse.warehouse_code})
            {t?.exceptions > 0 && <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full bg-rose-100 text-rose-800" data-testid="mcf-inv-exception-count">{t.exceptions} exception(s)</span>}
          </CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => load()} data-testid="mcf-inv-refresh"><RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />Refresh</Button>
            {isAdmin && <Button size="sm" className="bg-[#1F8F88] hover:bg-[#197a74] text-white" onClick={() => setAdjust({})} data-testid="mcf-inv-add-adjustment"><Plus className="h-4 w-4 mr-1" />Adjustment</Button>}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 mb-4 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input className="pl-10" placeholder="Search SKU ID or name..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="mcf-inv-search" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]" data-testid="mcf-inv-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="exceptions">Exceptions only</SelectItem>
                {Object.keys(STATUS_STYLE).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="overflow-x-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  {COLS.map((c) => <SortHead key={c[0]} col={c} sort={sort} setSort={setSort} />)}
                  <TableHead className="bg-slate-100 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageRows.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground" data-testid="mcf-inv-empty">
                    {data?.rows?.length ? "No SKUs match these filters." : "No delivered stock or invoiced consumption for this warehouse yet."}
                  </TableCell></TableRow>
                ) : pageRows.map((r) => (
                  <TableRow key={r.sku_id} className={r.available_qty < 0 ? "bg-rose-50/60" : ""} data-testid={`mcf-inv-row-${r.sku_id}`}>
                    <TableCell className="font-mono text-xs">{r.sku_id}</TableCell>
                    <TableCell>{r.sku_name || "—"}</TableCell>
                    <TableCell className="text-right font-mono" data-testid={`mcf-inv-delivered-${r.sku_id}`}>
                      {fmtQty(r.delivered_qty)}
                      {r.adjustment_qty !== 0 && <span className="block text-[10px] text-muted-foreground">incl. {r.adjustment_qty > 0 ? "+" : ""}{fmtQty(r.adjustment_qty)} adj.</span>}
                    </TableCell>
                    <TableCell className="text-right font-mono" data-testid={`mcf-inv-consumed-${r.sku_id}`}>{fmtQty(r.consumed_qty)}</TableCell>
                    <TableCell className={`text-right font-mono font-semibold ${r.available_qty < 0 ? "text-rose-600" : r.available_qty === 0 ? "text-slate-500" : "text-emerald-700"}`} data-testid={`mcf-inv-available-${r.sku_id}`}>{fmtQty(r.available_qty)}</TableCell>
                    <TableCell className="text-right font-mono" data-testid={`mcf-inv-pct-${r.sku_id}`}>{pct(r.consumption_pct)}</TableCell>
                    <TableCell><span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_STYLE[r.status]}`} data-testid={`mcf-inv-status-${r.sku_id}`}>{r.status}</span></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="View sources" onClick={() => setTraceSku(r.sku_id)} data-testid={`mcf-inv-view-${r.sku_id}`}><Eye className="h-4 w-4" /></Button>
                      {isAdmin && r.mapped && <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Adjust stock" onClick={() => setAdjust({ sku_id: r.sku_id, sku_name: r.sku_name })} data-testid={`mcf-inv-adjust-${r.sku_id}`}><SlidersHorizontal className="h-4 w-4" /></Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {rows.length > PAGE_SIZE && (
            <div className="flex items-center justify-between pt-3" data-testid="mcf-inv-pagination">
              <p className="text-sm text-muted-foreground">Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, rows.length)} of {rows.length}</p>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="mcf-inv-prev">Previous</Button>
                <span className="text-sm">Page {page} of {totalPages}</span>
                <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="mcf-inv-next">Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <McfSkuTraceDialog warehouse={warehouse} skuId={traceSku} isAdmin={isAdmin} onClose={() => setTraceSku(null)} onChanged={() => load(true)} />
      <McfAdjustDialog warehouse={warehouse} target={adjust} products={products} onClose={() => setAdjust(null)} onSaved={() => { setAdjust(null); load(true); }} />
    </div>
  );
}
